import { useState, useEffect, useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { MessageSquare, ChevronDown, ChevronUp, Loader2, Search, User, Bot } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChatLogsTab({ adminToken }) {
  const [sessions, setSessions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expandedSession, setExpandedSession] = useState(null);
  const [searchEmail, setSearchEmail] = useState("");

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "30" });
      if (searchEmail) params.set("email", searchEmail);
      const res = await axios.get(`${API}/admin/chat-logs?${params}`, { headers: { Authorization: `Bearer ${adminToken}` } });
      setSessions(res.data.sessions || []);
      setTotal(res.data.total || 0);
    } catch (err) { if (err.response?.status === 401) window.location.reload(); }
    setLoading(false);
  }, [searchEmail, adminToken]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  return (
    <div className="space-y-4" data-testid="chat-logs-tab">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Filter by email..." value={searchEmail}
            onChange={e => setSearchEmail(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") fetchLogs(); }}
            className="pl-9 text-sm" data-testid="chat-search-input" />
        </div>
        <span className="text-sm text-slate-500">{total} conversation{total !== 1 ? "s" : ""}</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : sessions.length === 0 ? (
        <div className="text-center py-16 text-slate-400">No AI chat conversations yet.</div>
      ) : (
        <div className="space-y-2">
          {sessions.map(s => (
            <div key={s.session_id} className="border rounded-lg bg-white overflow-hidden">
              <button
                onClick={() => setExpandedSession(expandedSession === s.session_id ? null : s.session_id)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50 transition"
                data-testid={`chat-session-${s.session_id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <MessageSquare className="w-4 h-4 text-indigo-500 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">
                      {s.business_name || s.place_id || "General Chat"}
                    </p>
                    <p className="text-xs text-slate-400">{s.message_count} messages · {new Date(s.last_message_at).toLocaleString()}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px]">{s.place_id === "global" ? "Global" : "Report"}</Badge>
                  {expandedSession === s.session_id ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                </div>
              </button>
              {expandedSession === s.session_id && (
                <div className="border-t bg-slate-50 p-4 space-y-3 max-h-[400px] overflow-y-auto">
                  {(s.messages || []).map((m, i) => (
                    <div key={`${m.created_at}-${i}`} className={`flex gap-2.5 ${m.role === "assistant" ? "" : "flex-row-reverse"}`}>
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${m.role === "assistant" ? "bg-indigo-100" : "bg-slate-200"}`}>
                        {m.role === "assistant" ? <Bot className="w-3.5 h-3.5 text-indigo-600" /> : <User className="w-3.5 h-3.5 text-slate-600" />}
                      </div>
                      <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${m.role === "assistant" ? "bg-white border text-slate-700" : "bg-indigo-600 text-white"}`}>
                        {m.content}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
